import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";

// Phase 17-D item 8 (org side): pending DofE/UCAS assessor-report requests.
// Prefilled draft is editable; approve/decline notifies the volunteer once.
// Renders nothing when there's no pending request.
export function AssessorRequestsCard() {
  const [requests, setRequests] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.assessorRequests().then(setRequests).catch(() => {});
  useEffect(() => { load(); }, []);

  const pending = requests.filter((r) => r.status === "pending");
  if (pending.length === 0) return null;

  const respond = async (r, action) => {
    setBusy(r.id);
    try {
      await api.respondAssessorRequest(r.id, action, drafts[r.id] ?? r.draft);
      toast.success(action === "approve"
        ? `Report approved — ${r.volunteer_name} has been told`
        : "Request declined — the volunteer has been told");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-8 bg-white border border-neutral-200 rounded-2xl p-6" data-testid="assessor-requests-card">
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
        <GraduationCap className="w-4 h-4 text-terracotta" /> Assessor report requests
      </div>
      <p className="text-sm text-neutral-500 mt-1.5">
        Volunteers doing DofE/UCAS need a short assessor report — we've prefilled a draft from their approved hours. Edit and approve, or decline.
      </p>
      <div className="mt-4 space-y-4">
        {pending.map((r) => (
          <div key={r.id} className="border border-neutral-100 rounded-xl p-4" data-testid={`assessor-request-${r.id}`}>
            <div className="text-sm font-medium text-forest">{r.volunteer_name}</div>
            {r.note && <div className="text-xs text-neutral-500 mt-1">"{r.note}"</div>}
            <Textarea
              data-testid={`assessor-draft-${r.id}`}
              value={drafts[r.id] ?? r.draft}
              onChange={(e) => setDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))}
              className="mt-3 text-sm h-32"
            />
            <div className="flex gap-2 mt-3">
              <Button
                data-testid={`assessor-approve-${r.id}`}
                disabled={busy === r.id}
                onClick={() => respond(r, "approve")}
                className="rounded-full h-9 px-5 text-xs bg-forest hover:bg-forest-hover"
              >
                Approve report
              </Button>
              <Button
                data-testid={`assessor-decline-${r.id}`}
                disabled={busy === r.id}
                onClick={() => respond(r, "decline")}
                variant="ghost"
                className="rounded-full h-9 px-4 text-xs text-neutral-500 hover:text-red-600"
              >
                Decline
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
